 
import setAddedToCartModalOpen from "@iewcasia/Store/Handlers/Custom/CustomSetAddedToCartModal";
import setSpoolModalOpen from "@iewcasia/Store/Handlers/Custom/CustomSetSpoolModal";
import useCustomQuoteRequired from "@iewcasia/Store/Handlers/Utilities/useCustomQuoteRequired";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

// Verndale Customization
interface CustomOwnProps {
    isCustomQuoteRequired?: boolean;
}

type Props = OwnProps &
    CustomOwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    const cartLines = getCurrentCartState(state).value?.cartLines;
    const page = getCurrentPage(state);
    const pageType = page?.type;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        showAddedToCartModal: (getSettingsCollection(state) as any).customCartSettings?.showCustomAddToCartModal === true,
        addToCartPopupTimeout,
        cartLines,
        pageType,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    // Verndale Customization
    setSpoolModalOpen,
    setAddedToCartModalOpen,
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

// Verndale Custom Feature START - Styles for validation message for qtyOnHand
export interface ProductAddToCartButtonStyles {
    popupWrapper?: InjectableCss;
    popupMessageText?: TypographyProps;
    popupMessageTitle?: TypographyProps;
    closeButton?: ButtonPresentationProps;
    actionLink?: LinkPresentationProps;
}

export const productAddToCartButtonPopupStyles: ProductAddToCartButtonStyles = {
    popupWrapper: {},
    popupMessageText: {},
    popupMessageTitle: {},
    closeButton: {},
    actionLink: {},
};

// Verndale Custom Feature END - Styles for validation message for qtyOnHand

const ProductAddToCartButton = ({
    productContext: {
        product,
        productInfo,
        product: { id: productId, allowZeroPricing, quoteRequired, properties: productProperties },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory },
    },
    cartLines,
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    showAddedToCartModal,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    // Verndale Customization
    setSpoolModalOpen,
    setAddedToCartModalOpen,
    isCustomQuoteRequired,
    pageType,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    // Verndale Custom Feature - Adding validation message for qtyOnHand
    const [popupStyles] = useState(() => mergeToNew(productAddToCartButtonPopupStyles, extendedStyles));
    const [isQtyValid, setIsQtyValid] = useState(true);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    quoteRequired = useCustomQuoteRequired({
        product,
        productInfo,
    });

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const addToCartClickHandler = async (options?: { forceQuoteRequired?: boolean }) => {
        const effectiveQuoteRequired = !!options?.forceQuoteRequired || quoteRequired;
        // Verndale Customization -Parse spools and spoolSelected
        const spoolsLength = pricing?.additionalResults?.spoolLength ?? null;
        const spoolSelected = pricing?.additionalResults?.spoolSelected ?? null;
        const { allowSpoolOptions, isBackOrder } = product.properties;
        /* Verndale Custom Feature - Define properties object to be added to cartline */
        const properties: { [key: string]: string } = {};

        /* Verndale Custom Feature - Validate qtyOnHand */
        if (
            inventory?.qtyOnHand &&
            inventory?.qtyOnHand < qtyOrdered &&
            !isPopupOpen &&
            !effectiveQuoteRequired &&
            !isBackOrder
        ) {
            setIsQtyValid(false);
            setIsPopupOpen(true);

            return;
        }

        // If the item has spool from the RealTimePricingResult, we add it to the properties
        if (spoolSelected && (allowSpoolOptions === "true" || pageType === "MyListsDetailsPage")) {
            if (spoolSelected !== null && isQtyValid) {
                properties["spoolSelected"] = spoolSelected;
            } else {
                properties["spoolSelected"] = "1";
            }
        }

        // If the item has spoolLength from the RealTimePricingResult, we add it to the properties
        if (spoolsLength && (allowSpoolOptions === "true" || pageType === "MyListsDetailsPage")) {
            if(pageType !== "MyListsDetailsPage"){
                if (spoolsLength !== null && isQtyValid) {
                    properties["spoolLength"] = spoolsLength;
                } else {
                    properties["spoolLength"] = qtyOrdered.toString();
                }
            }else{
                properties["spoolLength"] = qtyOrdered.toString();
            }   
          
        }

        // if this product already exists in the cartlines with the properties spool,
        // we dont add it again, instead we display a modal
        if (cartLines && cartLines.length > 0) {
            const cartLine = cartLines.find(
                cl =>
                    cl.productId === productId &&
                    cl.properties &&
                    cl.properties.spoolLength !== undefined &&
                    cl.properties.spoolSelected !== undefined &&
                    cl.properties.spoolLength !== "" &&
                    cl.properties.spoolSelected !== "",
            );

            if (cartLine) {
                setSpoolModalOpen({ isOpen: true });
                return;
            }
        }

        onAddToCartClick?.();

        if (!effectiveQuoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        // List of property keys to check
        const propertyKeys = ["isQuotedByUser", "looseEnds", "isCustNCNR", "isBackOrder","deliveryLeadTimeWeeks"];

        // Iterate over the keys and add them to properties if they exist and have a value
        /* Verndale Custom Feature - Add Open Dll's change customization */
        /* Verndale Custom Feature - Add loose ends customization */
        /* Verndale Custom Feature - Add ncnr customization */
        propertyKeys.forEach(key => {
            if (productProperties[key] != null && productProperties[key] !== "") {
                properties[key] = productProperties[key];
            }
        });

        if (effectiveQuoteRequired) {
            properties["isQuotedByUser"] = "true";
        }

        const existingQtyOrdered =
            cartLines?.find(
                cl =>
                    cl.productId === productId &&
                    (cl.unitOfMeasure || "").toLowerCase() === (unitOfMeasure || "").toLowerCase(),
            )?.qtyOrdered ?? 0;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            properties,
        } as any)) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            if (showAddedToCartModal) {
                // Keep availability / quote flags on the cart line even if the API omits them,
                // so AddedToCartModal matches PDP ProductAvailability.
                const cartLineForModal: CartLineModel = {
                    ...cartline,
                    quoteRequired: effectiveQuoteRequired || !!cartline.quoteRequired,
                    properties: {
                        ...cartline.properties,
                        ...properties,
                    },
                };
                const productForModal = {
                    ...product,
                    quoteRequired: effectiveQuoteRequired || !!product.quoteRequired,
                    properties: {
                        ...product.properties,
                        ...properties,
                    },
                };
                setAddedToCartModalOpen({
                    isOpen: true,
                    cartLine: cartLineForModal,
                    originalQtyOrdered: qtyOrdered,
                    existingQtyOrdered,
                    isQtyAdjusted: cartline.isQtyAdjusted,
                    product: productForModal,
                    productInfo,
                    forceQuoteRequired: !!options?.forceQuoteRequired,
                });
            } else {
                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                    messageType: "success",
                    timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                });
            }

            setIsPopupOpen(false); // Verndale Custom Feature - Add validation message for qtyOnHand
            setIsQtyValid(true); // Verndale Custom Feature - Add validation message for qtyOnHand
        }
    };

    // Verndale Custom Feature - Adding available qty to cart
    const addAvailableQty = () => {
        setTimeout(() => {
            qtyOrdered = inventory?.qtyOnHand ?? 0;
            addToCartClickHandler();
        }, 300);
    };

    // Verndale Custom Feature - Adding request a quote (force quote-required UI in AddedToCartModal)
    const requestAQuoteClickHandler = () => {
        setTimeout(() => {
            addToCartClickHandler({ forceQuoteRequired: true });
        }, 300);
    };

    /* Verndale Custom Feature - close validation popup */
    const closePopup = () => {
        setIsQtyValid(true);
        setIsPopupOpen(false);
    };

    /* Verndale Custom Feature - check if the popup is open, if so detect click outside of popup and close it */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (event.target instanceof Element && !event.target.closest(".popup-wrapper")) {
                setIsQtyValid(true);
                setIsPopupOpen(false);
            }
        };
        if (isPopupOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isPopupOpen]);

    /* Verndale Custom Feature - setting general styles for popup container */
    const containerStyles = {
        css: css`
            position: absolute;
            height: 40px;
            width: 100%;
            max-width: 250px;
            pointer-events: none;
        `,
    };

    return (
        <>
            {/* Verndale Custom Feature start - Add validation message for qtyOnHand */}
            <StyledWrapper {...containerStyles}>
                {!isQtyValid && (
                    <StyledWrapper className="popup-wrapper" {...popupStyles.popupWrapper}>
                        <Button {...popupStyles.closeButton} onClick={closePopup} title="Close Popup"></Button>
                        <Typography {...popupStyles.popupMessageTitle}>
                            {siteMessage("AddToCart_Validation_Title_Tooltip")}
                        </Typography>
                        <Typography {...popupStyles.popupMessageText}>
                            {siteMessage("AddToCart_Validation_Content_Tooltip")}
                        </Typography>
                        {inventory?.qtyOnHand && inventory?.qtyOnHand < qtyOrdered && (
                            <Link
                                href=""
                                onClick={addAvailableQty}
                                {...popupStyles.actionLink}
                                title="Go to Qty Ordered"
                            >{`${translate("Add Available Quantity")} (${
                                inventory?.qtyOnHand
                            } ${unitOfMeasure})`}</Link>
                        )}
                        <Link
                            href=""
                            onClick={requestAQuoteClickHandler}
                            {...popupStyles.actionLink}
                            title="Go to Request Quote"
                        >
                            {translate("Request a Quote")}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {/* Verndale Custom Feature end - Add validation message for qtyOnHand */}
            <Button
                {...styles}
                onClick={() => addToCartClickHandler()}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                {...otherProps}
            >
                {/* Verndale Custom Feature - Add translation for Add to Cart & Request Quote */}
                {labelOverride ?? translate(quoteRequired ? "Request Quote" : "Add to Cart")}
            </Button>
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
